import { For, createEffect, createMemo, createSignal, onCleanup, type JSX } from "solid-js";

interface VirtualListProps<T> {
  /** Total list of items. Only the visible window is rendered. */
  items: readonly T[];
  /**
   * Fixed row height in CSS pixels. Used when `itemHeights` is omitted.
   * If both are provided, `itemHeights` wins.
   */
  itemHeight: number;
  /**
   * Optional per-row heights in CSS pixels, same length as `items`. When
   * provided, the list runs in variable-height mode: cumulative offsets are
   * used for positioning and a binary search picks the first visible row.
   */
  itemHeights?: readonly number[];
  /**
   * Viewport height in CSS pixels. If omitted, the component measures its
   * own scroll container via ResizeObserver. For tests, pass it explicitly.
   */
  viewportHeight?: number;
  /** Extra rows to render above/below the visible window (smooths scroll). */
  overscan?: number;
  /** Render one item; receives the item and its index in `items`. */
  renderItem: (item: T, index: number) => JSX.Element;
  /**
   * Optional ref-style callback called with the scroll container element so
   * the caller can imperatively read/write scrollTop (for sync-scroll).
   */
  onScrollerRef?: (el: HTMLDivElement) => void;
  /**
   * Optional scroll handler; called with the current scrollTop. The list
   * also handles its own visibility windowing internally — the consumer
   * only needs this to mirror scroll to another VirtualList.
   */
  onScroll?: (scrollTop: number) => void;
  /**
   * Optional row-range overlays drawn on top of rows. Each overlay spans
   * `rowStart` (inclusive) to `rowEnd` (exclusive). Pointer events are
   * disabled so overlays don't block scrolling.
   */
  overlays?: ReadonlyArray<{ rowStart: number; rowEnd: number; class: string }>;
}

export function VirtualList<T>(props: VirtualListProps<T>): JSX.Element {
  const [scrollTop, setScrollTop] = createSignal(0);
  const [measuredHeight, setMeasuredHeight] = createSignal(
    props.viewportHeight ?? 0,
  );
  // scroller.scrollWidth — total horizontal content extent. Overlays use this
  // so a current-hunk underline reaches the right edge of the longest line
  // (not just the viewport edge) and stays visible while scrolling sideways.
  const [scrollWidth, setScrollWidth] = createSignal(0);

  let scroller: HTMLDivElement | undefined;
  let ro: ResizeObserver | undefined;

  const measureScrollWidth = () => {
    if (scroller) setScrollWidth(scroller.scrollWidth);
  };

  const setScroller = (el: HTMLDivElement) => {
    scroller = el;
    props.onScrollerRef?.(el);
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver((entries) => {
        for (const e of entries) {
          if (props.viewportHeight === undefined) {
            setMeasuredHeight(e.contentRect.height);
          }
        }
        measureScrollWidth();
      });
      ro.observe(el);
      if (props.viewportHeight === undefined) setMeasuredHeight(el.clientHeight);
      measureScrollWidth();
    }
  };

  onCleanup(() => { ro?.disconnect(); });

  const effectiveHeight = () =>
    props.viewportHeight !== undefined ? props.viewportHeight : measuredHeight();

  const overscan = () => props.overscan ?? 4;

  // Cumulative offsets: offsets[i] = sum of heights[0..i-1]; offsets[N] = totalHeight.
  // In fixed-height mode this stays undefined and we use arithmetic instead.
  const offsets = createMemo<number[] | undefined>(() => {
    const heights = props.itemHeights;
    if (!heights) return undefined;
    const out = new Array<number>(heights.length + 1);
    out[0] = 0;
    for (let i = 0; i < heights.length; i++) {
      out[i + 1] = (out[i] as number) + (heights[i] as number);
    }
    return out;
  });

  // Binary search: largest i with offsets[i] <= target.
  const findRowAt = (offs: number[], target: number): number => {
    let lo = 0;
    let hi = offs.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if ((offs[mid] as number) <= target) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  };

  // Visible row window: clamp to [0, items.length).
  const window_ = createMemo(() => {
    const total = props.items.length;
    if (total === 0) return { start: 0, end: 0 };
    const vh = effectiveHeight();
    const offs = offsets();
    if (offs) {
      if (vh <= 0) return { start: 0, end: Math.min(total, overscan()) };
      const top = scrollTop();
      const firstVisible = findRowAt(offs, top);
      const lastVisible = findRowAt(offs, top + vh);
      const start = Math.max(0, firstVisible - overscan());
      const end = Math.min(total, lastVisible + 1 + overscan());
      return { start, end };
    }
    if (props.itemHeight <= 0) return { start: 0, end: 0 };
    if (vh <= 0) return { start: 0, end: Math.min(total, overscan()) };
    const firstVisible = Math.floor(scrollTop() / props.itemHeight);
    const visibleCount = Math.ceil(vh / props.itemHeight);
    const start = Math.max(0, firstVisible - overscan());
    const end = Math.min(total, firstVisible + visibleCount + overscan());
    return { start, end };
  });

  const visibleItems = createMemo(() => {
    const { start, end } = window_();
    const out: { item: T; index: number }[] = [];
    for (let i = start; i < end; i++) {
      out.push({ item: props.items[i] as T, index: i });
    }
    return out;
  });

  const totalHeight = () => {
    const offs = offsets();
    if (offs) return offs[offs.length - 1] as number;
    return props.items.length * props.itemHeight;
  };

  const topFor = (index: number): number => {
    const offs = offsets();
    if (offs) return offs[index] as number;
    return index * props.itemHeight;
  };

  const heightFor = (index: number): number => {
    const heights = props.itemHeights;
    if (heights) return heights[index] as number;
    return props.itemHeight;
  };

  const onScroll = (_e: Event) => {
    if (!scroller) return;
    const st = scroller.scrollTop;
    setScrollTop(st);
    props.onScroll?.(st);
  };

  // Re-measure scrollWidth after the visible window changes — a newly-rendered
  // longer row may grow scrollWidth, and overlays need to track it.
  createEffect(() => {
    visibleItems();
    queueMicrotask(measureScrollWidth);
  });

  return (
    <div
      ref={setScroller}
      class="fd-vl-scroller"
      onScroll={onScroll}
      style={{ overflow: "auto", height: "100%", position: "relative" }}
    >
      <div
        class="fd-vl-spacer"
        style={{ height: `${totalHeight()}px`, position: "relative" }}
      >
        <For each={visibleItems()}>
          {(entry) => (
            <div
              class="fd-vl-row"
              style={{
                position: "absolute",
                top: `${topFor(entry.index)}px`,
                left: 0,
                // Stretch to scroller.scrollWidth so row backgrounds
                // (.fd-line-change-*, .fd-line-delete/insert) cover the full
                // horizontal extent of the longest line — without this the
                // bg ends at viewport edge and is missing when scrolled right.
                width: scrollWidth() > 0 ? `${scrollWidth()}px` : "100%",
                height: `${heightFor(entry.index)}px`,
              }}
            >
              {props.renderItem(entry.item, entry.index)}
            </div>
          )}
        </For>
        <For each={props.overlays ?? []}>
          {(o) => (
            <div
              class={o.class}
              style={{
                position: "absolute",
                top: `${topFor(o.rowStart)}px`,
                left: 0,
                // Stretch to scroller.scrollWidth so a current-hunk underline
                // reaches past the viewport edge to the longest row's end.
                // Fall back to 100% before the first measurement.
                width: scrollWidth() > 0 ? `${scrollWidth()}px` : "100%",
                height: `${topFor(o.rowEnd) - topFor(o.rowStart)}px`,
                "pointer-events": "none",
              }}
            />
          )}
        </For>
      </div>
    </div>
  );
}
